import React, { useState } from 'react';
import {
  AFFILIATE_MEMBERSHIP,
  MEMBERSHIP_HERO,
  PUBLIC_ACCESS,
  REGULAR_MEMBERSHIP,
  WHY_AGENCIES_JOIN,
  WORKFLOW_COMPARISON,
  WORKFLOW_COLUMNS,
  WORKFLOW_SECTION,
  type WorkflowStep,
} from './homePageConstants';
import { MembershipCostComparisonModal } from './MembershipCostComparisonModal';
import { WhyAgencyBenefitIcon } from './WhyAgencyBenefitIcon';
import './MembershipCostComparisonModal.css';

function WorkflowSteps({ steps }: { steps: readonly WorkflowStep[] }) {
  return (
    <ol className="home-membership__workflow-steps">
      {steps.map((step, index) => (
        <li key={step.text}>
          <span className="home-membership__workflow-icon" aria-hidden="true">
            {step.icon}
          </span>
          <span className="home-membership__workflow-step-text">
            <span className="home-membership__workflow-step-number">{index + 1}.</span> {step.text}
          </span>
        </li>
      ))}
    </ol>
  );
}

export const PublicVsMembershipSection: React.FC = () => {
  const [costComparisonOpen, setCostComparisonOpen] = useState(false);

  return (
    <>
      <section
        id="public-vs-membership"
        className="home-section home-membership"
        aria-labelledby="public-vs-membership-heading"
      >
        <div className="home-section__container">
          <header className="home-membership__hero">
            <h2 id="public-vs-membership-heading" className="home-membership__title">
              {MEMBERSHIP_HERO.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {index > 0 ? <br /> : null}
                  {line}
                </React.Fragment>
              ))}
            </h2>
            <p className="home-membership__value-prop">{MEMBERSHIP_HERO.tagline}</p>
            {MEMBERSHIP_HERO.paragraphs.map((paragraph, index) => (
              <p
                key={paragraph}
                className={`home-membership__subtitle${index === 0 ? ' home-membership__subtitle--mission' : ''}`}
              >
                {paragraph}
              </p>
            ))}
            <aside className="home-membership__goal-callout" aria-label="Membership goal">
              <p className="home-membership__goal-label">{MEMBERSHIP_HERO.goalCallout.label}</p>
              <p className="home-membership__goal-text">
                <span className="home-membership__goal-prefix">{MEMBERSHIP_HERO.goalCallout.prefix} </span>
                <strong className="home-membership__goal-emphasis">{MEMBERSHIP_HERO.goalCallout.emphasis}</strong>
                <span className="home-membership__goal-suffix"> {MEMBERSHIP_HERO.goalCallout.suffix}</span>
              </p>
            </aside>
            <div className="home-membership__cost-link-wrap">
              <button
                type="button"
                className="home-btn home-btn--outline"
                onClick={() => setCostComparisonOpen(true)}
              >
                Compare Membership Costs
              </button>
            </div>
          </header>

          <section className="home-membership__tiers" aria-labelledby="membership-tiers-heading">
            <h3 id="membership-tiers-heading" className="home-membership__section-title">
              Membership Options
            </h3>
            <div className="home-compare home-compare--three">
              <article className="home-compare__column home-compare__column--public">
                <p className="home-compare__tier">{PUBLIC_ACCESS.tierLabel}</p>
                <h4 className="home-compare__heading">{PUBLIC_ACCESS.heading}</h4>
                <p className="home-compare__intro">{PUBLIC_ACCESS.description}</p>
                <ul className="home-compare__list home-compare__list--compact">
                  {PUBLIC_ACCESS.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>

              <article className="home-compare__column home-compare__column--affiliate">
                <p className="home-compare__tier">{AFFILIATE_MEMBERSHIP.tierLabel}</p>
                <h4 className="home-compare__heading home-compare__heading--emphasis">
                  {AFFILIATE_MEMBERSHIP.heading}
                </h4>
                <p className="home-compare__price">{AFFILIATE_MEMBERSHIP.priceLabel}</p>
                <p className="home-compare__intro">{AFFILIATE_MEMBERSHIP.intro}</p>
                <ul className="home-compare__list home-compare__list--compact">
                  {AFFILIATE_MEMBERSHIP.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>

              <article className="home-compare__column home-compare__column--regular">
                <span className="home-compare__popular-badge">Most Popular</span>
                <p className="home-compare__tier">{REGULAR_MEMBERSHIP.tierLabel}</p>
                <h4 className="home-compare__heading home-compare__heading--emphasis">
                  {REGULAR_MEMBERSHIP.heading}
                </h4>
                <p className="home-compare__price">{REGULAR_MEMBERSHIP.priceLabel}</p>
                <p className="home-compare__intro">{REGULAR_MEMBERSHIP.intro}</p>
                <ul className="home-compare__list home-compare__list--compact">
                  {REGULAR_MEMBERSHIP.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            </div>
          </section>
        </div>
      </section>

      <section
        id="filling-the-gaps"
        className="home-section home-section--muted home-membership__gaps"
        aria-labelledby="filling-the-gaps-heading"
      >
        <div className="home-section__container">
          <header className="home-section__header">
            <h2 id="filling-the-gaps-heading" className="home-section__title">
              {WORKFLOW_SECTION.title}
            </h2>
            <p className="home-section__subtitle">{WORKFLOW_SECTION.subtitle}</p>
          </header>

          <div id="climate-action-workflow" className="home-membership__workflow-columns">
            <div className="home-card-grid home-card-grid--three home-workflow-columns-grid">
              {WORKFLOW_COLUMNS.map((column) => (
                <article key={column.title} className="home-workflow-card home-workflow-card--flow">
                  <h3 className="home-workflow-card__title">{column.title}</h3>
                  <ul className="home-workflow-card__list">
                    {column.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>

          <div
            id="workflow-comparison"
            className="home-membership__workflow"
            aria-labelledby="workflow-comparison-heading"
          >
            <h3 id="workflow-comparison-heading" className="home-membership__section-title">
              {WORKFLOW_COMPARISON.title}
            </h3>
            <div className="home-membership__workflow-grid">
              <article className="home-membership__workflow-card home-membership__workflow-card--traditional">
                <h4>{WORKFLOW_COMPARISON.traditional.label}</h4>
                <WorkflowSteps steps={WORKFLOW_COMPARISON.traditional.items} />
              </article>
              <article className="home-membership__workflow-card home-membership__workflow-card--platform">
                <h4>{WORKFLOW_COMPARISON.climatePlans.label}</h4>
                <WorkflowSteps steps={WORKFLOW_COMPARISON.climatePlans.items} />
              </article>
            </div>
          </div>

          <div
            id="why-agencies-join"
            className="home-membership__why"
            aria-labelledby="why-agencies-join-heading"
          >
            <h3 id="why-agencies-join-heading" className="home-membership__section-title">
              {WHY_AGENCIES_JOIN.title}
            </h3>
            <div className="home-membership__benefit-grid">
              {WHY_AGENCIES_JOIN.items.map((item) => (
                <article key={item.text} className="home-membership__benefit-card">
                  <WhyAgencyBenefitIcon icon={item.icon} />
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MembershipCostComparisonModal
        isOpen={costComparisonOpen}
        onClose={() => setCostComparisonOpen(false)}
      />
    </>
  );
};
